import { faClipboardCheck, faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Button, ConfirmModal, Modal, Well } from '@feathr/components';
import { cssVar } from '@feathr/hooks';

import { useAPIKeySecretModal } from './APIKeySecretModal.hooks';

export interface IAPIKeySecretModalProps {
  /**
   * Called when the modal is allowed to close: Done, a close attempt after copying, or a
   * confirmed close-without-copying. The parent must discard the secret when this fires —
   * it can never be displayed again.
   */
  onClose: () => void;
  opened: boolean;
  /** The one-time plaintext key value from the create response. */
  secret: string;
}

/**
 * One-time display of a newly created service API key. The secret is only available on the
 * create response, so the user must copy it before leaving: Done stays disabled until the
 * key is copied, and closing beforehand asks for confirmation.
 */
function APIKeySecretModal({
  onClose,
  opened,
  secret,
}: Readonly<IAPIKeySecretModalProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const {
    handleCloseAttempt,
    handleCloseWarningBack,
    handleCloseWarningConfirm,
    handleCopy,
    hasCopied,
    isCloseWarningOpen,
    showCopiedFeedback,
  } = useAPIKeySecretModal({ onClose, opened, secret });

  return (
    <Modal
      closeOnClickOutside={false}
      onClose={handleCloseAttempt}
      opened={opened}
      rightActions={[
        <Button key={'cancel'} name={'api-key-secret-cancel'} onClick={handleCloseAttempt}>
          {t('Cancel')}
        </Button>,
        <Button
          disabled={!hasCopied}
          key={'done'}
          name={'api-key-secret-done'}
          onClick={onClose}
          tooltip={!hasCopied ? t('Copy your key to continue.') : undefined}
          type={'primary'}
        >
          {t('Done')}
        </Button>,
      ]}
      title={t('Save your Feathr API key')}
    >
      <Flex direction={'column'} gap={theme.spacing['4']}>
        <Alert
          description={t(
            "Copy this key now and store it somewhere safe. For your security, you won't be able to see it again.",
          )}
          name={'api-key-secret-warning'}
          title={t('Make sure to copy your key now')}
          type={'warning'}
        />
        <Well description={secret} layout={'horizontal'} name={'api-key-secret'} width={'full'}>
          <Button
            aria-label={showCopiedFeedback ? t('Copied') : t('Copy key')}
            name={'api-key-secret-copy'}
            onClick={handleCopy}
            type={'icon'}
          >
            <FontAwesomeIcon
              color={showCopiedFeedback ? cssVar('--color-success') : undefined}
              icon={showCopiedFeedback ? faClipboardCheck : faCopy}
            />
          </Button>
        </Well>
      </Flex>
      <ConfirmModal
        alertDescription={t(
          'For your security, this key cannot be recovered or shown again once this window is closed.',
        )}
        alertTitle={t("You won't be able to see this key again")}
        cancelButtonText={t('Go back')}
        confirmButtonText={t('Close without copying')}
        confirmButtonType={'danger'}
        onClose={handleCloseWarningBack}
        onConfirm={handleCloseWarningConfirm}
        opened={isCloseWarningOpen}
        t={t}
        title={t('Close without copying your key?')}
      />
    </Modal>
  );
}

export default APIKeySecretModal;
